import React, { useEffect, useState, useCallback } from 'react';
import { fetchAuthSession } from 'aws-amplify/auth';

const BASE = "https://ltwph7gpvb.execute-api.us-east-1.amazonaws.com";

const INCOTERMS = ["EXW", "FCA", "FAS", "FOB", "CFR", "CIF", "CPT", "CIP", "DAP", "DPU", "DDP"];
const STAGES = ["OFFER", "TERMS_SHARED", "NEGOTIATION", "AMENDMENT", "LC_ISSUED",
  "SHIPPED", "DOCS_PRESENTED", "SETTLED", "CLOSED"];
const ROLES = ["seller", "buyer", "financier"];
const LC_DOCS = ["BL", "INVOICE", "COO", "QUALITY", "SGS_QQ", "CUSTOMS", "INSURANCE",
  "PACKING_LIST", "CONFORMITY"];
const DEFAULT_TERMS = [
  { key: "incoterm", label: "Incoterm", value: "", visibleTo: ROLES },
  { key: "pricePerMT", label: "Price per MT (USD)", value: "", visibleTo: ["seller", "financier"] },
  { key: "quantity", label: "Quantity and tolerance", value: "", visibleTo: ROLES },
  { key: "payment", label: "Payment terms", value: "", visibleTo: ROLES },
  { key: "laytime", label: "Laytime and demurrage", value: "", visibleTo: ROLES }
];

const pretty = (s) => String(s || "").replace(/_/g, " ").toLowerCase().replace(/^./, (c) => c.toUpperCase());
const statusColor = { ok: "var(--green)", warn: "var(--amber)", error: "var(--red)" };
const statusText = { ok: "OK", warn: "Check", error: "Problem" };

async function authHeaders() {
  const session = await fetchAuthSession();
  const token = session.tokens?.idToken?.toString();
  return { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };
}

async function call(method, path, body) {
  const res = await fetch(`${BASE}${path}`, {
    method,
    headers: await authHeaders(),
    body: body ? JSON.stringify(body) : undefined
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || `Error ${res.status}`);
  return data;
}

const api = {
  list: () => call("GET", "/deals"),
  get: (id) => call("GET", `/deals/${id}`),
  create: (d) => call("POST", "/deals", d),
  update: (id, d) => call("PUT", `/deals/${id}`, d),
  addTerms: (id, d) => call("POST", `/deals/${id}/terms`, d),
  addLC: (id, d) => call("POST", `/deals/${id}/lcs`, d)
};

const box = { border: "1px solid var(--line)", borderRadius: 8, padding: 12, margin: "10px 0" };
const row = { display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", margin: "10px 0" };

/* ---------- New deal ---------- */
function NewDeal({ onCancel, onCreated }) {
  const [f, setF] = useState({
    dealRef: "", commodity: "", incoterm: "CIF", quantityMT: "", tolerancePct: "",
    loadPort: "", dischargePort: "", notes: "",
    parties: { seller: { name: "", email: "" }, buyer: { name: "", email: "" }, financier: { name: "", email: "" } }
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const setParty = (role, k) => (e) =>
    setF({ ...f, parties: { ...f.parties, [role]: { ...f.parties[role], [k]: e.target.value } } });

  async function save() {
    setBusy(true); setError("");
    try {
      const deal = await api.create({
        dealRef: f.dealRef, commodity: f.commodity, incoterm: f.incoterm,
        quantityMT: Number(f.quantityMT), tolerancePct: Number(f.tolerancePct),
        loadPort: f.loadPort, dischargePort: f.dischargePort,
        parties: f.parties, internal: { notes: f.notes }
      });
      onCreated(deal.dealId);
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div className="card">
      <h2>New deal</h2>
      {error && <div className="err">{error}</div>}
      <div className="form">
        <div><label>Deal reference</label><input value={f.dealRef} onChange={set("dealRef")} /></div>
        <div><label>Commodity</label><input value={f.commodity} onChange={set("commodity")} /></div>
        <div><label>Incoterm</label>
          <select value={f.incoterm} onChange={set("incoterm")}>
            {INCOTERMS.map((i) => <option key={i}>{i}</option>)}
          </select>
        </div>
        <div><label>Quantity (MT)</label><input type="number" value={f.quantityMT} onChange={set("quantityMT")} /></div>
        <div><label>Tolerance (%)</label><input type="number" value={f.tolerancePct} onChange={set("tolerancePct")} /></div>
        <div><label>Load port</label><input value={f.loadPort} onChange={set("loadPort")} /></div>
        <div><label>Discharge port</label><input value={f.dischargePort} onChange={set("dischargePort")} /></div>
      </div>
      <h2 style={{ marginTop: 20 }}>Parties</h2>
      <p className="note">External users see a deal only when their login email matches the email entered here.</p>
      <div className="form">
        {ROLES.map((r) => (
          <React.Fragment key={r}>
            <div><label>{pretty(r)} name</label><input value={f.parties[r].name} onChange={setParty(r, "name")} /></div>
            <div><label>{pretty(r)} email</label><input type="email" value={f.parties[r].email} onChange={setParty(r, "email")} /></div>
          </React.Fragment>
        ))}
      </div>
      <h2 style={{ marginTop: 20 }}>Internal notes</h2>
      <p className="note">Visible to your team only. Never shown to seller, buyer or financier.</p>
      <textarea rows={3} style={{ width: "100%" }} value={f.notes} onChange={set("notes")} />
      <div style={row}>
        <button className="btn" onClick={save} disabled={busy || !f.dealRef}>{busy ? "Saving..." : "Create deal"}</button>
        <button className="link" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
}

/* ---------- Terms ---------- */
function TermsPanel({ dealId, data, canEdit, onSaved }) {
  const current = data.terms?.terms || [];
  const [editing, setEditing] = useState(false);
  const [rows, setRows] = useState([]);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const startEdit = () => {
    setRows(current.length ? current.map((t) => ({ ...t })) : DEFAULT_TERMS.map((t) => ({ ...t })));
    setNote(""); setEditing(true);
  };
  const patch = (i, k, v) => setRows(rows.map((r, idx) => (idx === i ? { ...r, [k]: v } : r)));
  const toggleRole = (i, role) => {
    const has = (rows[i].visibleTo || []).includes(role);
    patch(i, "visibleTo", has ? rows[i].visibleTo.filter((x) => x !== role) : [...(rows[i].visibleTo || []), role]);
  };

  async function save() {
    setBusy(true); setError("");
    try {
      const terms = rows.filter((r) => r.label).map((r) => ({
        ...r, key: r.key || r.label.toLowerCase().replace(/[^a-z0-9]+/g, "_")
      }));
      await api.addTerms(dealId, { terms, note });
      setEditing(false); onSaved();
    } catch (e) { setError(e.message); }
    setBusy(false);
  }

  return (
    <div className="card">
      <div style={row}>
        <h2 style={{ margin: 0 }}>Terms {data.terms ? `(version ${data.terms.version})` : ""}</h2>
        {canEdit && !editing && <button className="btn" onClick={startEdit}>{current.length ? "Create new version" : "Add terms"}</button>}
      </div>
      {error && <div className="err">{error}</div>}

      {!editing && (current.length === 0 ? <p className="note">No terms recorded yet.</p> : (
        <div className="tablewrap">
          <table>
            <thead><tr><th>Term</th><th>Value</th>{canEdit && <th>Visible to</th>}</tr></thead>
            <tbody>
              {current.map((t) => (
                <tr key={t.key}>
                  <td>{t.label}</td><td>{String(t.value)}</td>
                  {canEdit && <td>{(t.visibleTo || []).map(pretty).join(", ") || "Team only"}</td>}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ))}

      {editing && (
        <div style={box}>
          {rows.map((r, i) => (
            <div key={i} style={row}>
              <input placeholder="Term" value={r.label} onChange={(e) => patch(i, "label", e.target.value)} />
              <input placeholder="Value" value={r.value} onChange={(e) => patch(i, "value", e.target.value)} style={{ flex: 1, minWidth: 160 }} />
              <span>
                {ROLES.map((role) => (
                  <label key={role} style={{ marginRight: 10 }}>
                    <input type="checkbox" checked={(r.visibleTo || []).includes(role)} onChange={() => toggleRole(i, role)} /> {pretty(role)}
                  </label>
                ))}
              </span>
              <button className="link" onClick={() => setRows(rows.filter((_, idx) => idx !== i))}>Remove</button>
            </div>
          ))}
          <button className="link" onClick={() => setRows([...rows, { key: "", label: "", value: "", visibleTo: ROLES }])}>Add term</button>
          <div style={row}>
            <input placeholder="What changed in this version?" value={note} onChange={(e) => setNote(e.target.value)} style={{ flex: 1 }} />
            <button className="btn" onClick={save} disabled={busy}>{busy ? "Saving..." : "Save as new version"}</button>
            <button className="link" onClick={() => setEditing(false)} disabled={busy}>Cancel</button>
          </div>
        </div>
      )}

      {data.history?.length > 0 && (
        <details style={{ marginTop: 12 }}>
          <summary>Version history</summary>
          {data.history.map((h) => (
            <div key={h.version} style={box}>
              <b>Version {h.version}</b> <span className="note">{new Date(h.createdAt).toLocaleString()} by {h.changedBy}</span>
              {h.note && <div>{h.note}</div>}
              {(h.changes || []).map((c) => (
                <div key={c.key} className="note">{c.key}: {String(c.from ?? "none")} to {String(c.to ?? "removed")}</div>
              ))}
            </div>
          ))}
        </details>
      )}
    </div>
  );
}

/* ---------- LCs ---------- */
function AddLC({ dealId, deal, onSaved, onCancel }) {
  const [f, setF] = useState({
    type: "MASTER", lcNumber: "", issuingBank: "", beneficiary: "", currency: "USD", amount: "",
    quantityMT: deal.quantityMT || "", goodsDescription: deal.commodity || "", incoterm: deal.incoterm || "",
    paymentDays: "", issueDate: "", expiryDate: "", latestShipmentDate: "", presentationDays: "", requiredDocs: []
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const toggleDoc = (d) =>
    setF({ ...f, requiredDocs: f.requiredDocs.includes(d) ? f.requiredDocs.filter((x) => x !== d) : [...f.requiredDocs, d] });

  async function save() {
    setBusy(true); setError("");
    try {
      await api.addLC(dealId, {
        ...f, amount: Number(f.amount), quantityMT: Number(f.quantityMT),
        paymentDays: Number(f.paymentDays), presentationDays: Number(f.presentationDays), status: "DRAFT"
      });
      onSaved();
    } catch (e) { setError(e.message); setBusy(false); }
  }

  return (
    <div style={box}>
      {error && <div className="err">{error}</div>}
      <div className="form">
        <div><label>LC type</label>
          <select value={f.type} onChange={set("type")}>
            {["MASTER", "BACK_TO_BACK", "DIRECT"].map((t) => <option key={t} value={t}>{pretty(t)}</option>)}
          </select>
        </div>
        <div><label>LC number</label><input value={f.lcNumber} onChange={set("lcNumber")} /></div>
        <div><label>Issuing bank</label><input value={f.issuingBank} onChange={set("issuingBank")} /></div>
        <div><label>Beneficiary</label><input value={f.beneficiary} onChange={set("beneficiary")} /></div>
        <div><label>Amount ({f.currency})</label><input type="number" value={f.amount} onChange={set("amount")} /></div>
        <div><label>Quantity (MT)</label><input type="number" value={f.quantityMT} onChange={set("quantityMT")} /></div>
        <div><label>Goods description</label><input value={f.goodsDescription} onChange={set("goodsDescription")} /></div>
        <div><label>Incoterm</label>
          <select value={f.incoterm} onChange={set("incoterm")}>
            <option value="">Not stated</option>{INCOTERMS.map((i) => <option key={i}>{i}</option>)}
          </select>
        </div>
        <div><label>Days to pay after presentation (banking)</label><input type="number" value={f.paymentDays} onChange={set("paymentDays")} /></div>
        <div><label>Presentation period (days)</label><input type="number" value={f.presentationDays} onChange={set("presentationDays")} /></div>
        <div><label>Issue date</label><input type="date" value={f.issueDate} onChange={set("issueDate")} /></div>
        <div><label>Latest shipment date</label><input type="date" value={f.latestShipmentDate} onChange={set("latestShipmentDate")} /></div>
        <div><label>Expiry date</label><input type="date" value={f.expiryDate} onChange={set("expiryDate")} /></div>
      </div>
      <fieldset style={{ margin: "12px 0", border: "1px solid var(--line)", borderRadius: 6 }}>
        <legend>Documents required</legend>
        {LC_DOCS.map((d) => (
          <label key={d} style={{ marginRight: 14, display: "inline-block" }}>
            <input type="checkbox" checked={f.requiredDocs.includes(d)} onChange={() => toggleDoc(d)} /> {pretty(d)}
          </label>
        ))}
      </fieldset>
      <div style={row}>
        <button className="btn" onClick={save} disabled={busy}>{busy ? "Saving..." : "Save LC"}</button>
        <button className="link" onClick={onCancel} disabled={busy}>Cancel</button>
      </div>
    </div>
  );
}

function LCPanel({ dealId, deal, lcs, alignment, canEdit, onSaved }) {
  const [adding, setAdding] = useState(false);
  const order = { MASTER: 0, BACK_TO_BACK: 1, DIRECT: 2 };
  const sorted = [...lcs].sort((a, b) => order[a.type] - order[b.type]);

  return (
    <div className="card">
      <div style={row}>
        <h2 style={{ margin: 0 }}>Letters of credit</h2>
        {canEdit && !adding && <button className="btn" onClick={() => setAdding(true)}>Add LC</button>}
      </div>
      {adding && <AddLC dealId={dealId} deal={deal} onSaved={() => { setAdding(false); onSaved(); }} onCancel={() => setAdding(false)} />}

      {sorted.length === 0 ? <p className="note">No LCs recorded yet.</p> : (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(280px, 1fr))", gap: 12 }}>
          {sorted.map((lc) => (
            <div style={box} key={lc.lcId}>
              <b>{pretty(lc.type)} LC</b> <span className="badge other">{pretty(lc.status)}</span>
              <div>{lc.lcNumber || "No LC number yet"}</div>
              <div className="note">{lc.issuingBank} to {lc.beneficiary}</div>
              <div>{lc.currency} {Number(lc.amount).toLocaleString()} for {Number(lc.quantityMT).toLocaleString()} MT</div>
              <div>Latest shipment {lc.latestShipmentDate || "not set"}; expires {lc.expiryDate || "not set"}</div>
              <div>{lc.paymentDays ? `Pays ${lc.paymentDays} banking days after presentation` : "Payment days not set"}</div>
              <div className="note">Documents: {(lc.requiredDocs || []).map(pretty).join(", ") || "none listed"}</div>
            </div>
          ))}
        </div>
      )}

      {alignment && (
        <>
          <h2 style={{ marginTop: 20 }}>Master and back-to-back checks</h2>
          {alignment.note && <p className="note">{alignment.note}</p>}
          {alignment.checks?.length > 0 && (
            <>
              {typeof alignment.margin === "number" && (
                <p>Gap between master and back-to-back amounts: <b>{alignment.margin.toLocaleString()}</b></p>
              )}
              <ul className="list">
                {alignment.checks.map((c, i) => (
                  <li key={i} style={{ marginBottom: 6 }}>
                    <b style={{ color: statusColor[c.status] }}>{statusText[c.status]}:</b> {c.message}
                  </li>
                ))}
              </ul>
            </>
          )}
        </>
      )}
    </div>
  );
}

/* ---------- Documents ---------- */
const DOC_STAGES = {
  A_PRE_LC: "A. Before the LC is issued",
  B_SHIPPING: "B. Shipping documents",
  C_VESSEL: "C. Vessel and discharge notices",
  D_OTHER: "D. Other documents"
};
const NEEDED = { PRE_LC: "Before LC", CONTRACT: "Contract", LC: "LC", NOTICE: "Notice" };
const DOC_COLOR = {
  PENDING: "var(--amber)", RECEIVED: "var(--accent)", CHECKED: "var(--green)",
  DISCREPANCY: "var(--red)", WAIVED: "inherit"
};

function RoleChecks({ label: text, value, onChange }) {
  return (
    <span style={{ marginRight: 16 }}>
      <b>{text}:</b>{" "}
      {ROLES.map((r) => (
        <label key={r} style={{ marginRight: 8 }}>
          <input type="checkbox" checked={value.includes(r)}
            onChange={() => onChange(value.includes(r) ? value.filter((x) => x !== r) : [...value, r])} /> {pretty(r)}
        </label>
      ))}
    </span>
  );
}

function AccessEditor({ doc, onSave, onCancel }) {
  const [uploadBy, setUploadBy] = useState(doc.uploadBy || []);
  const [visibleTo, setVisibleTo] = useState(doc.visibleTo || []);
  return (
    <div style={box}>
      <p className="note" style={{ marginTop: 0 }}>Your team can always see and upload. Choose which outside parties can too.</p>
      <RoleChecks label="Can upload" value={uploadBy} onChange={setUploadBy} />
      <RoleChecks label="Can see" value={visibleTo} onChange={setVisibleTo} />
      <div style={row}>
        <button className="btn" onClick={() => onSave(uploadBy, visibleTo)}>Save access</button>
        <button className="link" onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}

function DocsPanel({ dealId, isStaff }) {
  const [docs, setDocs] = useState(null);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState("");
  const [accessFor, setAccessFor] = useState(null);
  const [adding, setAdding] = useState(false);
  const blank = { name: "", stage: "D_OTHER", due: "", uploadBy: ["seller"], visibleTo: ROLES };
  const [newDoc, setNewDoc] = useState(blank);
  const base = `/deals/${dealId}/docs`;

  const load = useCallback(async () => {
    try { setDocs(await call("GET", base)); setError(""); }
    catch (e) { setError(e.message); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const run = async (id, fn) => {
    setBusyId(id); setError("");
    try { await fn(); await load(); } catch (e) { setError(e.message); }
    setBusyId("");
  };

  const upload = (doc, file) => run(doc.docId, async () => {
    if (!file) return;
    const type = file.type || "application/octet-stream";
    const { uploadUrl, fileId, key } = await call("POST", `${base}/${doc.docId}/upload-url`,
      { fileName: file.name, contentType: type, size: file.size });
    const put = await fetch(uploadUrl, { method: "PUT", headers: { "Content-Type": type }, body: file });
    if (!put.ok) throw new Error("Upload to storage failed. Check the storage bucket CORS settings.");
    await call("POST", `${base}/${doc.docId}/files`, { fileId, key, fileName: file.name });
  });

  const download = (doc, f) => run(doc.docId, async () => {
    const { url } = await call("GET", `${base}/${doc.docId}/files/${f.fileId}/download-url`);
    const a = document.createElement("a");
    a.href = url; a.rel = "noopener";
    document.body.appendChild(a); a.click(); a.remove();
  });

  const update = (doc, changes) => run(doc.docId, () => call("PUT", `${base}/${doc.docId}`, changes));
  const flag = (doc) => {
    const note = window.prompt("What is the discrepancy?");
    if (note) update(doc, { status: "DISCREPANCY", discrepancyNote: note });
  };

  if (!docs) {
    return <div className="card"><h2>Documents</h2>{error ? <div className="err">{error}</div> : <p>Loading documents...</p>}</div>;
  }

  const active = docs.filter((d) => d.status !== "WAIVED");
  const count = (s) => active.filter((d) => d.status === s).length;

  return (
    <div className="card">
      <div style={row}>
        <h2 style={{ margin: 0 }}>Documents</h2>
        {isStaff && <button className="btn" onClick={() => run("tpl", () => call("POST", base, { template: true }))} disabled={busyId === "tpl"}>Load checklist template</button>}
        {isStaff && !adding && <button className="link" onClick={() => setAdding(true)}>Add document</button>}
      </div>
      {error && <div className="err">{error}</div>}

      {docs.length > 0 && (
        <p className="note">
          {active.length} documents tracked: {count("PENDING")} pending, {count("RECEIVED")} received,
          {" "}{count("CHECKED")} checked, {count("DISCREPANCY")} with discrepancy.
        </p>
      )}

      {adding && (
        <div style={box}>
          <div className="form">
            <div><label>Document name</label><input value={newDoc.name} onChange={(e) => setNewDoc({ ...newDoc, name: e.target.value })} /></div>
            <div><label>Group</label>
              <select value={newDoc.stage} onChange={(e) => setNewDoc({ ...newDoc, stage: e.target.value })}>
                {Object.entries(DOC_STAGES).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label>Due (optional)</label><input value={newDoc.due} onChange={(e) => setNewDoc({ ...newDoc, due: e.target.value })} /></div>
          </div>
          <div style={{ marginTop: 8 }}>
            <RoleChecks label="Can upload" value={newDoc.uploadBy} onChange={(v) => setNewDoc({ ...newDoc, uploadBy: v })} />
            <RoleChecks label="Can see" value={newDoc.visibleTo} onChange={(v) => setNewDoc({ ...newDoc, visibleTo: v })} />
          </div>
          <div style={row}>
            <button className="btn" disabled={!newDoc.name || busyId === "add"}
              onClick={() => run("add", async () => { await call("POST", base, newDoc); setAdding(false); setNewDoc(blank); })}>Add document</button>
            <button className="link" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {docs.length === 0 && (
        <p className="note">{isStaff ? "No documents yet. Load the checklist template to start, or add documents one by one." : "No documents have been shared with you on this deal yet."}</p>
      )}

      {Object.entries(DOC_STAGES).map(([stage, title]) => {
        const items = docs.filter((d) => d.stage === stage);
        if (items.length === 0) return null;
        return (
          <div key={stage}>
            <h2 style={{ marginTop: 20, fontSize: 15 }}>{title}</h2>
            <div className="tablewrap">
              <table>
                <thead><tr><th>Document</th><th>Needed for</th><th>Status</th><th>Files</th><th>Actions</th></tr></thead>
                <tbody>
                  {items.map((d) => (
                    <React.Fragment key={d.docId}>
                      <tr>
                        <td>
                          {d.name}
                          {d.due && <div className="note">Due: {d.due}</div>}
                          {d.discrepancyNote && <div style={{ color: "var(--red)" }}>Discrepancy: {d.discrepancyNote}</div>}
                        </td>
                        <td>{(d.requiredBy || []).map((r) => NEEDED[r] || r).join(", ") || "-"}</td>
                        <td><b style={{ color: DOC_COLOR[d.status] }}>{pretty(d.status)}</b></td>
                        <td>
                          {(d.files || []).length === 0 ? <span className="note">None yet</span> : d.files.map((f) => (
                            <div key={f.fileId}>
                              <button className="link" onClick={() => download(d, f)} disabled={busyId === d.docId}>{f.fileName}</button>
                              <span className="note"> {pretty(f.uploadedByRole)}, {new Date(f.uploadedAt).toLocaleDateString()}</span>
                            </div>
                          ))}
                        </td>
                        <td>
                          {busyId === d.docId && <span className="note">Working... </span>}
                          {d.canUpload && (
                            <label style={{ color: "var(--accent)", cursor: "pointer", textDecoration: "underline", marginRight: 10 }}>
                              Upload
                              <input type="file" style={{ display: "none" }} disabled={busyId === d.docId}
                                onChange={(e) => { upload(d, e.target.files[0]); e.target.value = ""; }} />
                            </label>
                          )}
                          {d.canReview && d.status === "RECEIVED" && <button className="link" onClick={() => update(d, { status: "CHECKED" })}>Mark checked</button>}
                          {d.canReview && d.status !== "WAIVED" && (d.files || []).length > 0 && d.status !== "DISCREPANCY" && <button className="link" onClick={() => flag(d)}>Flag discrepancy</button>}
                          {isStaff && <button className="link" onClick={() => setAccessFor(accessFor === d.docId ? null : d.docId)}>Access</button>}
                          {isStaff && d.status !== "WAIVED" && <button className="link" onClick={() => update(d, { status: "WAIVED" })}>Waive</button>}
                          {isStaff && d.status === "WAIVED" && <button className="link" onClick={() => update(d, { status: "PENDING" })}>Restore</button>}
                        </td>
                      </tr>
                      {accessFor === d.docId && (
                        <tr><td colSpan={5}>
                          <AccessEditor doc={d} onCancel={() => setAccessFor(null)}
                            onSave={(uploadBy, visibleTo) => run(d.docId, async () => {
                              await call("PUT", `${base}/${d.docId}`, { uploadBy, visibleTo });
                              setAccessFor(null);
                            })} />
                        </td></tr>
                      )}
                    </React.Fragment>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}

/* ---------- Amendments ---------- */
const AM_COLOR = {
  OPEN: "var(--amber)", ACCEPTED: "var(--green)", REJECTED: "var(--red)",
  WITHDRAWN: "inherit", APPLIED: "var(--accent)"
};
const tagRole = (r) => (r === "staff" ? "team" : r);
const ACTION_TEXT = {
  propose: "proposed", accept: "accepted", reject: "rejected", counter: "countered",
  withdraw: "withdrew", apply: "applied to terms"
};

function AmendmentsPanel({ dealId, isStaff, terms, onApplied }) {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [adding, setAdding] = useState(false);
  const blank = { termKey: terms[0]?.key || "__other", termLabel: "", proposedValue: "", reason: "", to: ["seller"], onBehalfOf: "" };
  const [f, setF] = useState(blank);
  const [respondAs, setRespondAs] = useState({});
  const base = `/deals/${dealId}/amendments`;

  const load = useCallback(async () => {
    try { setItems(await call("GET", base)); setError(""); }
    catch (e) { setError(e.message); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const run = async (id, fn) => {
    setBusy(id); setError("");
    try { await fn(); await load(); } catch (e) { setError(e.message); }
    setBusy("");
  };
  const act = (a, action, extra = {}) => run(a.amendmentId, async () => {
    await call("PUT", `${base}/${a.amendmentId}`, { action, ...extra });
    if (action === "apply") onApplied();
  });

  const who = (a) => (isStaff ? (respondAs[a.amendmentId] || a.respondAs[0]) : undefined);
  const accept = (a) => act(a, "accept", { onBehalfOf: who(a) });
  const reject = (a) => {
    const comment = window.prompt("Why is this rejected? (optional)");
    if (comment !== null) act(a, "reject", { comment, onBehalfOf: who(a) });
  };
  const counter = (a) => {
    const counterValue = window.prompt("Your counter proposal (new value):");
    if (!counterValue) return;
    const comment = window.prompt("Comment (optional)") || "";
    act(a, "counter", { counterValue, comment, onBehalfOf: who(a) });
  };

  const submit = () => run("new", async () => {
    const other = f.termKey === "__other";
    const body = {
      termKey: other ? "" : f.termKey,
      termLabel: other ? f.termLabel : "",
      proposedValue: f.proposedValue,
      reason: f.reason
    };
    if (isStaff) { body.to = f.to; body.onBehalfOf = f.onBehalfOf; }
    await call("POST", base, body);
    setAdding(false); setF(blank);
  });


  return (
    <div className="card">
      <div style={row}>
        <h2 style={{ margin: 0 }}>Amendments</h2>
        {!adding && <button className="btn" onClick={() => { setF({ ...blank, termKey: terms[0]?.key || "__other" }); setAdding(true); }}>Request an amendment</button>}
      </div>
      <p className="note">Propose a change to a term. The other side accepts, rejects or counters, and your team applies agreed changes as a new terms version.</p>
      {error && <div className="err">{error}</div>}

      {adding && (
        <div style={box}>
          <div className="form">
            <div><label>Term</label>
              <select value={f.termKey} onChange={(e) => setF({ ...f, termKey: e.target.value })}>
                {terms.map((t) => <option key={t.key} value={t.key}>{t.label} (now: {String(t.value)})</option>)}
                <option value="__other">Other term (type below)</option>
              </select>
            </div>
            {f.termKey === "__other" && (
              <div><label>Term name</label><input value={f.termLabel} onChange={(e) => setF({ ...f, termLabel: e.target.value })} /></div>
            )}
            <div><label>Proposed value</label><input value={f.proposedValue} onChange={(e) => setF({ ...f, proposedValue: e.target.value })} /></div>
            <div><label>Reason</label><input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
          </div>
          {isStaff && (
            <div style={{ marginTop: 8 }}>
              <RoleChecks label="Send to" value={f.to} onChange={(v) => setF({ ...f, to: v })} />
              <label>Raised for:{" "}
                <select value={f.onBehalfOf} onChange={(e) => setF({ ...f, onBehalfOf: e.target.value })}>
                  <option value="">Our team</option>
                  {ROLES.map((r) => <option key={r} value={r}>{pretty(r)}</option>)}
                </select>
              </label>
            </div>
          )}
          <div style={row}>
            <button className="btn" onClick={submit} disabled={busy === "new" || !f.proposedValue || (f.termKey === "__other" && !f.termLabel)}>
              {busy === "new" ? "Sending..." : "Send amendment"}
            </button>
            <button className="link" onClick={() => setAdding(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!items ? <p>Loading amendments...</p> : items.length === 0 ? <p className="note">No amendments yet.</p> : items.map((a) => (
        <div style={box} key={a.amendmentId}>
          <div>
            <b>{a.termLabel}</b>: {a.currentValue === null || a.currentValue === undefined ? "new term" : String(a.currentValue)} to <b>{String(a.proposedValue)}</b>{" "}
            <b style={{ color: AM_COLOR[a.status] }}>{pretty(a.status)}</b>
          </div>
          {a.reason && <div className="note">Reason: {a.reason}</div>}
          {a.status === "OPEN" && <div className="note">Waiting for: {(a.awaiting || []).map((r) => pretty(tagRole(r))).join(", ")}</div>}
          {a.status === "APPLIED" && <div className="note">Applied in terms version {a.appliedVersion}</div>}

          <div style={row}>
            {busy === a.amendmentId && <span className="note">Working...</span>}
            {a.canRespond && isStaff && a.respondAs.length > 0 && (
              <label>Respond as{" "}
                <select className="sel" value={respondAs[a.amendmentId] || a.respondAs[0]}
                  onChange={(e) => setRespondAs({ ...respondAs, [a.amendmentId]: e.target.value })}>
                  {a.respondAs.map((r) => <option key={r} value={r}>{pretty(tagRole(r))}</option>)}
                </select>
              </label>
            )}
            {a.canRespond && <button className="btn" onClick={() => accept(a)} disabled={busy === a.amendmentId}>Accept</button>}
            {a.canRespond && <button className="link" onClick={() => counter(a)} disabled={busy === a.amendmentId}>Counter</button>}
            {a.canRespond && <button className="link" onClick={() => reject(a)} disabled={busy === a.amendmentId}>Reject</button>}
            {a.canWithdraw && <button className="link" onClick={() => act(a, "withdraw")} disabled={busy === a.amendmentId}>Withdraw</button>}
            {a.canApply && <button className="btn" onClick={() => act(a, "apply")} disabled={busy === a.amendmentId}>Apply to terms</button>}
          </div>

          <details>
            <summary>History ({(a.rounds || []).length})</summary>
            {(a.rounds || []).map((r, i) => (
              <div key={i} className="note">
                {new Date(r.at).toLocaleString()}: {pretty(tagRole(r.onBehalfOf || r.role))}
                {r.onBehalfOf ? " (recorded by team)" : ""} {ACTION_TEXT[r.action] || r.action}
                {r.value !== undefined && r.value !== null ? ` ${String(r.value)}` : ""}
                {r.comment ? ` - ${r.comment}` : ""}
              </div>
            ))}
          </details>
        </div>
      ))}
    </div>
  );
}

/* ---------- Conversation ---------- */
const CHANNEL_HINT = {
  ALL: "Everyone on this deal sees these messages: seller, buyer, financier and your team.",
  SELLER: "Only the seller and your team see these messages.",
  BUYER: "Only the buyer and your team see these messages.",
  FINANCIER: "Only the financier and your team see these messages.",
  INTERNAL: "Only your team sees these messages."
};

function ConversationPanel({ dealId, isStaff, role }) {
  const own = (role || "").toUpperCase();
  const channels = isStaff ? ["ALL", "SELLER", "BUYER", "FINANCIER", "INTERNAL"] : ["ALL", own];
  const [channel, setChannel] = useState("ALL");
  const [msgs, setMsgs] = useState(null);
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const boxRef = React.useRef(null);
  const base = `/deals/${dealId}/messages`;

  const load = useCallback(async () => {
    try { setMsgs(await call("GET", base)); setError(""); }
    catch (e) { setError(e.message); }
  }, [base]);
  useEffect(() => {
    load();
    const t = setInterval(load, 30000);
    return () => clearInterval(t);
  }, [load]);

  const shown = (msgs || []).filter((m) => m.channel === channel);
  useEffect(() => {
    if (boxRef.current) boxRef.current.scrollTop = boxRef.current.scrollHeight;
  }, [shown.length, channel]);

  const name = (c) => {
    if (!isStaff && c === own) return "Private (you and our team)";
    return c === "ALL" ? "All parties" : c === "INTERNAL" ? "Internal (team only)" : pretty(c);
  };

  async function send() {
    setBusy(true); setError("");
    try { await call("POST", base, { channel, text }); setText(""); await load(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  }

  return (
    <div className="card">
      <div style={row}>
        <h2 style={{ margin: 0 }}>Conversation</h2>
        <button className="link" onClick={load}>Refresh</button>
      </div>
      <div style={row}>
        {channels.map((c) => (
          <button key={c} className={c === channel ? "btn" : "link"} onClick={() => setChannel(c)}>{name(c)}</button>
        ))}
      </div>
      <p className="note">{isStaff || channel === "ALL" ? CHANNEL_HINT[channel] : "Only you and the team see these messages."}</p>
      {error && <div className="err">{error}</div>}

      <div ref={boxRef} style={{ ...box, maxHeight: 320, overflowY: "auto" }}>
        {!msgs ? <span className="note">Loading messages...</span> : shown.length === 0 ? <span className="note">No messages yet.</span> : shown.map((m) => (
          <div key={m.msgId} style={{ marginBottom: 12 }}>
            <b>{m.mine ? "You" : isStaff ? `${m.authorEmail || ""} (${pretty(m.authorRole)})` : pretty(m.authorRole)}</b>{" "}
            <span className="note">{new Date(m.createdAt).toLocaleString()}</span>
            <div style={{ whiteSpace: "pre-wrap" }}>{m.text}</div>
          </div>
        ))}
      </div>
      <textarea rows={3} style={{ width: "100%" }} value={text} onChange={(e) => setText(e.target.value)}
        placeholder={`Write to: ${name(channel)}`} />
      <div style={row}>
        <button className="btn" onClick={send} disabled={busy || !text.trim()}>{busy ? "Sending..." : "Send"}</button>
      </div>
    </div>
  );
}

/* ---------- Money flow and profit ---------- */
const PAY_TYPE_TEXT = {
  SUPPLIER_PAYMENT: "Supplier payment", BUYER_RECEIPT: "Buyer receipt", COST: "Cost or charge",
  FINANCIER_SHARE: "Financier share", MARGIN: "Margin or commission", OTHER: "Other"
};
const PARTY_TEXT = {
  SELLER: "Seller", BUYER: "Buyer", FINANCIER: "Financier", MMNY: "MMNY (our team)", OTHER: "Other (bank, third party)"
};
const PAY_DEFAULT = {
  SUPPLIER_PAYMENT: ["FINANCIER", "SELLER"], BUYER_RECEIPT: ["BUYER", "FINANCIER"], COST: ["MMNY", "OTHER"],
  FINANCIER_SHARE: ["MMNY", "FINANCIER"], MARGIN: ["FINANCIER", "MMNY"], OTHER: ["MMNY", "OTHER"]
};
const money = (v) => (v === null || v === undefined ? "-" : Number(v).toLocaleString(undefined, { maximumFractionDigits: 2 }));
const PAY_COLOR = { PLANNED: "var(--amber)", ACTUAL: "var(--green)", CANCELLED: "inherit" };

function MoneyPanel({ dealId, deal, isStaff, role }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [busy, setBusy] = useState("");
  const [cfg, setCfg] = useState(null);
  const blankPay = { type: "SUPPLIER_PAYMENT", fromParty: "FINANCIER", toParty: "SELLER", amount: "", status: "PLANNED", plannedDate: "", actualDate: "", reference: "" };
  const [pay, setPay] = useState(null);
  const base = `/deals/${dealId}/money`;
  const sees = isStaff || role === "financier";

  const load = useCallback(async () => {
    try { setData(await call("GET", base)); setError(""); }
    catch (e) { setError(e.message); }
  }, [base]);
  useEffect(() => { load(); }, [load]);

  const run = async (id, fn) => {
    setBusy(id); setError("");
    try { await fn(); await load(); } catch (e) { setError(e.message); }
    setBusy("");
  };

  if (!data) {
    if (!sees) return null;
    return <div className="card"><h2>Cash flow and profit</h2>{error ? <div className="err">{error}</div> : <p>Loading...</p>}</div>;
  }
  if (!sees && data.payments.length === 0) {
    return <div className="card"><h2>Cash flow</h2><p className="note">No payments involving you have been recorded on this deal yet.</p></div>;
  }

  const s = data.summary;
  const startCfg = () => setCfg(data.config ? { ...data.config } : {
    model: "SPREAD", quantityMT: deal.quantityMT || "", actualQuantityMT: "", buyPricePerMT: "", sellPricePerMT: "",
    commissionPerMT: "", financierBasis: "PROFIT_SHARE", financierPct: "", feePeriod: "FLAT", financierSeesEconomics: true
  });
  const setC = (k) => (e) => setCfg({ ...cfg, [k]: e.target.value });
  const setP = (k) => (e) => setPay({ ...pay, [k]: e.target.value });
  const changeType = (e) => {
    const t = e.target.value;
    setPay({ ...pay, type: t, fromParty: PAY_DEFAULT[t][0], toParty: PAY_DEFAULT[t][1] });
  };
  const setStatus = (p, status) => run(p.payId, () => call("PUT", `${base}/payments/${p.payId}`, { status }));
  const confirm = (p) => run(p.payId, () => call("PUT", `${base}/payments/${p.payId}`, { action: "confirm" }));
  const c = data.config;
  const posRows = Object.entries(data.positions || {}).filter(([, v]) => v.actual !== 0 || v.planned !== 0);

  return (
    <div className="card">
      <h2>Cash flow and profit</h2>
      {error && <div className="err">{error}</div>}

      {s && !s.limited && (
        <div className="stats">
          <div className="stat"><span>Gross margin</span><b>{money(s.grossMargin)}</b></div>
          <div className="stat"><span>Costs</span><b>{money(s.costsTotal)}</b></div>
          <div className="stat"><span>Net margin</span><b>{money(s.netMargin)}</b></div>
          <div className="stat"><span>Net per MT</span><b>{money(s.netPerMT)}</b></div>
          <div className="stat"><span>Financier share</span><b>{money(s.financierShare)}</b></div>
          {isStaff && <div className="stat"><span>Your take</span><b>{money(s.yourTake)}</b></div>}
          <div className="stat"><span>Funded by financier</span><b>{money(s.funded)}</b></div>
          <div className="stat"><span>Funding exposure</span><b>{s.exposureDays === null ? "-" : `${s.exposureDays} days`}</b></div>
          <div className="stat"><span>Financier return</span><b>{s.returnPct === null ? "-" : `${s.returnPct}%`}{s.annualizedPct ? ` (${s.annualizedPct}% a year)` : ""}</b></div>
        </div>
      )}
      {s && s.limited && (
        <div className="stats">
          <div className="stat"><span>Funded by financier</span><b>{money(s.funded)}</b></div>
          <div className="stat"><span>Funding exposure</span><b>{s.exposureDays === null ? "-" : `${s.exposureDays} days`}</b></div>
          <div className="stat"><span>Financier share</span><b>{money(s.financierShare)}</b></div>
          <div className="stat"><span>Financier return</span><b>{s.returnPct === null ? "-" : `${s.returnPct}%`}</b></div>
        </div>
      )}
      {sees && !s && <p className="note">No economics set up for this deal yet.{isStaff ? " Use Set up economics below." : ""}</p>}

      {s?.alerts?.length > 0 && (
        <ul className="list">
          {s.alerts.map((a, i) => (
            <li key={i} style={{ color: a.level === "error" ? "var(--red)" : "var(--amber)", marginBottom: 4 }}>
              <b>{a.level === "error" ? "Problem" : "Check"}:</b> {a.message}
            </li>
          ))}
        </ul>
      )}

      {isStaff && (
        <div style={row}>
          {c && !cfg && (
            <span className="note">
              {c.model === "SPREAD"
                ? `Margin in the price: buy ${money(c.buyPricePerMT)}, sell ${money(c.sellPricePerMT)} per MT`
                : `Commission: ${money(c.commissionPerMT)} per MT`}
              {" / "}
              {c.financierBasis === "PROFIT_SHARE"
                ? `financier takes ${c.financierPct}% of net margin`
                : `financier fee ${c.financierPct}% of funded amount${c.feePeriod === "PER_30_DAYS" ? " per 30 days" : ""}`}
              {c.financierSeesEconomics === false ? " / financier sees funding only" : ""}
            </span>
          )}
          {!cfg && <button className="btn" onClick={startCfg}>{c ? "Edit economics" : "Set up economics"}</button>}
        </div>
      )}

      {cfg && (
        <div style={box}>
          <div className="form">
            <div><label>How you earn</label>
              <select value={cfg.model} onChange={setC("model")}>
                <option value="SPREAD">Margin built into the price (buy / sell)</option>
                <option value="COMMISSION">Commission per MT</option>
              </select>
            </div>
            <div><label>Quantity (MT)</label><input type="number" value={cfg.quantityMT} onChange={setC("quantityMT")} /></div>
            <div><label>Actual quantity (MT, optional)</label><input type="number" value={cfg.actualQuantityMT || ""} onChange={setC("actualQuantityMT")} /></div>
            {cfg.model === "SPREAD" ? (
              <>
                <div><label>Buy price per MT (from seller)</label><input type="number" value={cfg.buyPricePerMT} onChange={setC("buyPricePerMT")} /></div>
                <div><label>Sell price per MT (to buyer)</label><input type="number" value={cfg.sellPricePerMT} onChange={setC("sellPricePerMT")} /></div>
              </>
            ) : (
              <div><label>Commission per MT</label><input type="number" value={cfg.commissionPerMT} onChange={setC("commissionPerMT")} /></div>
            )}
            <div><label>Financier is paid by</label>
              <select value={cfg.financierBasis} onChange={setC("financierBasis")}>
                <option value="PROFIT_SHARE">Share of net margin</option>
                <option value="FUNDING_FEE">Fee on the amount funded</option>
              </select>
            </div>
            <div><label>Financier percentage (%)</label><input type="number" value={cfg.financierPct} onChange={setC("financierPct")} /></div>
            {cfg.financierBasis === "FUNDING_FEE" && (
              <div><label>Fee applies</label>
                <select value={cfg.feePeriod} onChange={setC("feePeriod")}>
                  <option value="FLAT">Once for the deal</option>
                  <option value="PER_30_DAYS">Per 30 days funded</option>
                </select>
              </div>
            )}
          </div>
          <label style={{ display: "block", marginTop: 8 }}>
            <input type="checkbox" checked={cfg.financierSeesEconomics !== false}
              onChange={(e) => setCfg({ ...cfg, financierSeesEconomics: e.target.checked })} /> Financier can see the full economics (margin and all payments)
          </label>
          <div style={row}>
            <button className="btn" disabled={busy === "cfg"}
              onClick={() => run("cfg", async () => {
                await call("PUT", `${base}/config`, {
                  ...cfg, quantityMT: Number(cfg.quantityMT), actualQuantityMT: Number(cfg.actualQuantityMT),
                  buyPricePerMT: Number(cfg.buyPricePerMT), sellPricePerMT: Number(cfg.sellPricePerMT),
                  commissionPerMT: Number(cfg.commissionPerMT), financierPct: Number(cfg.financierPct)
                });
                setCfg(null);
              })}>{busy === "cfg" ? "Saving..." : "Save economics"}</button>
            <button className="link" onClick={() => setCfg(null)}>Cancel</button>
          </div>
        </div>
      )}

      <div style={row}>
        <h2 style={{ margin: 0, fontSize: 15 }}>Payments</h2>
        {isStaff && !pay && <button className="btn" onClick={() => setPay({ ...blankPay })}>Add payment</button>}
      </div>

      {pay && (
        <div style={box}>
          <div className="form">
            <div><label>Type</label>
              <select value={pay.type} onChange={changeType}>
                {Object.entries(PAY_TYPE_TEXT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label>From</label>
              <select value={pay.fromParty} onChange={setP("fromParty")}>
                {Object.entries(PARTY_TEXT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label>To</label>
              <select value={pay.toParty} onChange={setP("toParty")}>
                {Object.entries(PARTY_TEXT).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <div><label>Amount</label><input type="number" value={pay.amount} onChange={setP("amount")} /></div>
            <div><label>Status</label>
              <select value={pay.status} onChange={setP("status")}>
                <option value="PLANNED">Planned</option><option value="ACTUAL">Paid</option>
              </select>
            </div>
            <div><label>Planned date</label><input type="date" value={pay.plannedDate} onChange={setP("plannedDate")} /></div>
            {pay.status === "ACTUAL" && <div><label>Paid date</label><input type="date" value={pay.actualDate} onChange={setP("actualDate")} /></div>}
            <div><label>Reference</label><input value={pay.reference} onChange={setP("reference")} /></div>
          </div>
          <div style={row}>
            <button className="btn" disabled={busy === "pay" || !pay.amount}
              onClick={() => run("pay", async () => { await call("POST", `${base}/payments`, { ...pay, amount: Number(pay.amount) }); setPay(null); })}>
              {busy === "pay" ? "Saving..." : "Save payment"}
            </button>
            <button className="link" onClick={() => setPay(null)}>Cancel</button>
          </div>
        </div>
      )}

      {data.payments.length === 0 ? <p className="note">No payments recorded yet.</p> : (
        <div className="tablewrap">
          <table>
            <thead><tr><th>Date</th><th>Payment</th><th>From to To</th><th>Amount</th><th>Status</th><th>Confirmed by</th><th>Actions</th></tr></thead>
            <tbody>
              {data.payments.map((p) => (
                <tr key={p.payId}>
                  <td>{p.status === "ACTUAL" ? p.actualDate : p.plannedDate || "-"}</td>
                  <td>{PAY_TYPE_TEXT[p.type]}{p.reference && <div className="note">{p.reference}</div>}</td>
                  <td>{PARTY_TEXT[p.fromParty]} to {PARTY_TEXT[p.toParty]}</td>
                  <td>{money(p.amount)} {p.currency}</td>
                  <td><b style={{ color: PAY_COLOR[p.status] }}>{p.status === "ACTUAL" ? "Paid" : pretty(p.status)}</b></td>
                  <td>{(p.confirmations || []).map((x) => pretty(x.role)).join(", ") || "-"}</td>
                  <td>
                    {p.canConfirm && <button className="link" onClick={() => confirm(p)} disabled={busy === p.payId}>Confirm</button>}
                    {isStaff && p.status === "PLANNED" && <button className="link" onClick={() => setStatus(p, "ACTUAL")} disabled={busy === p.payId}>Mark paid</button>}
                    {isStaff && p.status !== "CANCELLED" && <button className="link" onClick={() => setStatus(p, "CANCELLED")} disabled={busy === p.payId}>Cancel</button>}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {posRows.length > 0 && (
        <>
          <h2 style={{ marginTop: 20, fontSize: 15 }}>Net position (money in minus money out)</h2>
          <div className="tablewrap">
            <table>
              <thead><tr><th>Party</th><th>Paid so far</th><th>Including planned</th></tr></thead>
              <tbody>
                {posRows.map(([k, v]) => (
                  <tr key={k}><td>{PARTY_TEXT[k]}</td><td>{money(v.actual)}</td><td>{money(v.actual + v.planned)}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}

function PortfolioCard({ canEdit, onOpen }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    call("GET", "/money/portfolio").then(setData).catch(() => setData(false));
  }, []);
  if (data === null) return <div className="card"><p>Loading portfolio...</p></div>;
  if (data === false) return <div className="card"><div className="err">Could not load the portfolio.</div></div>;
  if (!data.rows.some((r) => r.hasEconomics)) {
    return (
      <div className="card">
        <h2>Portfolio cash flow</h2>
        <p className="note">No deals have economics set up yet. Open a deal, go to its Cash flow tab and choose Set up economics.</p>
      </div>
    );
  }
  const t = data.totals;
  return (
    <div className="card">
      <h2>Portfolio cash flow</h2>
      <div className="stats">
        <div className="stat"><span>Funded by financiers</span><b>{money(t.funded)}</b></div>
        <div className="stat"><span>Expected buyer receipts</span><b>{money(t.expectedReceipts)}</b></div>
        <div className="stat"><span>Net margin</span><b>{money(t.netMargin)}</b></div>
        <div className="stat"><span>Financier share</span><b>{money(t.financierShare)}</b></div>
        {canEdit && <div className="stat"><span>Your take</span><b>{money(t.yourTake)}</b></div>}
        <div className="stat"><span>Open alerts</span><b>{t.alerts}</b></div>
      </div>
      <div className="tablewrap">
        <table>
          <thead>
            <tr><th>Deal</th><th>Stage</th><th>Funded</th><th>Expected receipts</th><th>Net margin</th><th>Financier share</th>{canEdit && <th>Your take</th>}<th>Exposure</th><th>Alerts</th></tr>
          </thead>
          <tbody>
            {data.rows.filter((r) => r.hasEconomics).map((r) => (
              <tr key={r.dealId}>
                <td><button className="link" onClick={() => onOpen(r.dealId)}>{r.dealRef}</button></td>
                <td>{pretty(r.stage)}</td>
                <td>{money(r.funded)}</td>
                <td>{money(r.expectedReceipts)}</td>
                <td>{money(r.netMargin)}</td>
                <td>{money(r.financierShare)}</td>
                {canEdit && <td>{money(r.yourTake)}</td>}
                <td>{r.exposureDays === null ? "-" : `${r.exposureDays} days`}</td>
                <td>{r.alerts}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

/* ---------- Tabs ---------- */
function TabBar({ tabs, active, onChange }) {
  return (
    <div role="tablist" style={{ display: "flex", flexWrap: "wrap", gap: 2, borderBottom: "1px solid var(--line)", margin: "12px 0 16px" }}>
      {tabs.map((t) => {
        const on = t.id === active;
        return (
          <button key={t.id} role="tab" aria-selected={on} onClick={() => onChange(t.id)}
            style={{
              background: "none", border: "none", borderRadius: 0, cursor: "pointer", padding: "10px 14px",
              borderBottom: on ? "3px solid var(--accent)" : "3px solid transparent",
              fontWeight: on ? 700 : 400, color: "inherit", fontSize: "inherit", fontFamily: "inherit"
            }}>
            {t.label}
            {t.badge ? <span className="note" style={{ marginLeft: 6 }}>({t.badge})</span> : null}
          </button>
        );
      })}
    </div>
  );
}

/* ---------- Overview ---------- */
const plural = (n, w) => `${n} ${w}${n === 1 ? "" : "s"}`;
const LEVEL_ORDER = { error: 0, warn: 1, info: 2 };
const LEVEL_TEXT = { error: "Problem", warn: "To do", info: "Note" };
const LEVEL_COLOR = { error: "var(--red)", warn: "var(--amber)", info: "inherit" };

function OverviewTab({ d, data, extras, role, canEdit, onGo }) {
  const staff = canEdit;
  const reviewer = staff || role === "financier";
  const { docs, amendments: ams, money: mon } = extras;
  const items = [];
  const add = (level, text, tab) => items.push({ level, text, tab });

  if (docs) {
    const count = (f) => docs.filter(f).length;
    if (staff) {
      const pending = count((x) => x.status === "PENDING");
      if (pending) add("warn", `${plural(pending, "document")} still pending`, "docs");
    } else {
      const mine = count((x) => x.canUpload && ["PENDING", "DISCREPANCY"].includes(x.status));
      if (mine) add("warn", `${plural(mine, "document")} waiting for your upload`, "docs");
    }
    const toCheck = count((x) => x.status === "RECEIVED");
    if (reviewer && toCheck) add("warn", `${plural(toCheck, "received document")} to check`, "docs");
    const disc = count((x) => x.status === "DISCREPANCY");
    if (disc) add("error", `${plural(disc, "document")} with a discrepancy`, "docs");
  }
  if (ams) {
    const respond = ams.filter((a) => a.canRespond).length;
    if (respond) add("warn", staff ? `${plural(respond, "open amendment")} waiting for a response` : `${plural(respond, "amendment")} waiting for your response`, "terms");
    const ready = ams.filter((a) => a.canApply).length;
    if (ready) add("warn", `${plural(ready, "accepted amendment")} ready to apply to the terms`, "terms");
  }
  if (mon) {
    (mon.summary?.alerts || []).forEach((a) => add(a.level === "error" ? "error" : "warn", a.message, "cash"));
    const confirm = (mon.payments || []).filter((p) => p.canConfirm).length;
    if (confirm) add("warn", `${plural(confirm, "payment")} for you to confirm`, "cash");
    if (staff && !mon.config) add("info", "Economics are not set up for this deal yet", "cash");
  }
  (data.alignment?.checks || []).filter((c) => c.status !== "ok")
    .forEach((c) => add(c.status === "error" ? "error" : "warn", `LC check: ${c.message}`, "lcs"));
  if (reviewer && (data.lcs || []).length === 0) add("info", "No LCs recorded yet", "lcs");
  if (staff && !data.terms) add("info", "No terms recorded yet", "terms");
  items.sort((a, b) => LEVEL_ORDER[a.level] - LEVEL_ORDER[b.level]);

  const loaded = docs || ams || mon;
  const active = (docs || []).filter((x) => x.status !== "WAIVED");
  const done = active.filter((x) => ["RECEIVED", "CHECKED"].includes(x.status)).length;
  const s = mon?.summary;

  return (
    <>
      <div className="card">
        <h2>Needs attention</h2>
        {!loaded ? <p className="note">Checking this deal...</p> : items.length === 0 ? (
          <p className="note">Nothing needs attention right now.</p>
        ) : (
          <ul className="list">
            {items.map((i, idx) => (
              <li key={idx} style={{ marginBottom: 6 }}>
                <b style={{ color: LEVEL_COLOR[i.level] }}>{LEVEL_TEXT[i.level]}:</b> {i.text}{" "}
                <button className="link" onClick={() => onGo(i.tab)}>Open</button>
              </li>
            ))}
          </ul>
        )}
      </div>

      <div className="stats">
        <div className="stat"><span>Stage</span><b>{pretty(d.stage)}</b></div>
        <div className="stat"><span>Terms version</span><b>v{d.currentVersion}</b></div>
        {docs && <div className="stat"><span>Documents received or checked</span><b>{done} of {active.length}</b></div>}
        {ams && <div className="stat"><span>Open amendments</span><b>{ams.filter((a) => a.status === "OPEN").length}</b></div>}
        <div className="stat"><span>LCs recorded</span><b>{(data.lcs || []).length}</b></div>
        {s && !s.limited && <div className="stat"><span>Net margin</span><b>{money(s.netMargin)}</b></div>}
        {s && !s.limited && staff && <div className="stat"><span>Your take</span><b>{money(s.yourTake)}</b></div>}
        {s && <div className="stat"><span>Funded by financier</span><b>{money(s.funded)}</b></div>}
        {s && <div className="stat"><span>Funding exposure</span><b>{s.exposureDays === null ? "-" : `${s.exposureDays} days`}</b></div>}
      </div>

      <div className="card">
        <h2>Parties</h2>
        <div className="tablewrap">
          <table>
            <thead><tr><th>Role</th><th>Company</th><th>Email</th></tr></thead>
            <tbody>
              {ROLES.map((r) => (
                <tr key={r}><td>{pretty(r)}</td><td>{d.parties?.[r]?.name || "Not set"}</td><td>{d.parties?.[r]?.email || ""}</td></tr>
              ))}
            </tbody>
          </table>
        </div>
        {staff && d.internal?.notes && <div style={box}><b>Internal notes</b><div>{d.internal.notes}</div></div>}
      </div>
    </>
  );
}

/* ---------- Deal detail ---------- */
function DealDetail({ dealId, canEdit, role, onBack }) {
  const [data, setData] = useState(null);
  const [extras, setExtras] = useState({ docs: null, amendments: null, money: null });
  const [tab, setTab] = useState("overview");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try { setData(await api.get(dealId)); setError(""); }
    catch (e) { setError(e.message); }
  }, [dealId]);
  useEffect(() => { load(); }, [load]);

  // Light data for the Overview and tab counts; refreshed each time you switch tab
  const loadExtras = useCallback(async () => {
    const safe = (path) => call("GET", path).catch(() => null);
    const [docs, amendments, money] = await Promise.all([
      safe(`/deals/${dealId}/docs`), safe(`/deals/${dealId}/amendments`), safe(`/deals/${dealId}/money`)
    ]);
    setExtras({ docs, amendments, money });
  }, [dealId]);
  useEffect(() => { loadExtras(); }, [loadExtras, tab]);

  async function changeStage(stage) {
    setBusy(true);
    try { await api.update(dealId, { stage }); await load(); }
    catch (e) { setError(e.message); }
    setBusy(false);
  }

  if (!data) {
    return (
      <div className="card">
        <button className="link" onClick={onBack}>Back to deals</button>
        {error ? <div className="err">{error}</div> : <p>Loading deal...</p>}
      </div>
    );
  }
  const d = data.deal;

  const docs = extras.docs || [];
  const activeDocs = docs.filter((x) => x.status !== "WAIVED");
  const docsDone = activeDocs.filter((x) => ["RECEIVED", "CHECKED"].includes(x.status)).length;
  const openAm = (extras.amendments || []).filter((a) => a.status === "OPEN").length;
  const cashFlags = (extras.money?.summary?.alerts?.length || 0) + (extras.money?.payments || []).filter((p) => p.canConfirm).length;
  const lcProblems = (data.alignment?.checks || []).filter((c) => c.status === "error").length;

  const tabs = [
    { id: "overview", label: "Overview" },
    { id: "terms", label: "Terms & Amendments", badge: openAm ? `${openAm} open` : "" },
    { id: "docs", label: "Documents", badge: extras.docs && activeDocs.length ? `${docsDone}/${activeDocs.length}` : "" },
    { id: "lcs", label: "LCs", badge: lcProblems ? `${lcProblems} to fix` : "" },
    { id: "cash", label: "Cash flow", badge: cashFlags ? `${cashFlags} to check` : "" },
    { id: "chat", label: "Conversation" }
  ];

  return (
    <>
      <div className="card">
        <button className="link" onClick={onBack}>Back to deals</button>
        {error && <div className="err">{error}</div>}
        <h2 style={{ marginTop: 12 }}>{d.dealRef}: {d.commodity}</h2>
        <p>{d.incoterm} / {Number(d.quantityMT).toLocaleString()} MT {d.tolerancePct ? `(+/-${d.tolerancePct}%)` : ""} / {d.loadPort} to {d.dischargePort}</p>
        <div style={row}>
          <label>Stage</label>
          {canEdit ? (
            <select className="sel" value={d.stage} onChange={(e) => changeStage(e.target.value)} disabled={busy}>
              {STAGES.map((s) => <option key={s} value={s}>{pretty(s)}</option>)}
            </select>
          ) : <span className="badge other">{pretty(d.stage)}</span>}
        </div>
        <TabBar tabs={tabs} active={tab} onChange={setTab} />
      </div>

      {tab === "overview" && <OverviewTab d={d} data={data} extras={extras} role={role} canEdit={canEdit} onGo={setTab} />}
      {tab === "terms" && (
        <>
          <TermsPanel dealId={dealId} data={data} canEdit={canEdit} onSaved={load} />
          <AmendmentsPanel dealId={dealId} isStaff={canEdit} terms={data.terms?.terms || []} onApplied={load} />
        </>
      )}
      {tab === "docs" && <DocsPanel dealId={dealId} isStaff={canEdit} />}
      {tab === "lcs" && <LCPanel dealId={dealId} deal={d} lcs={data.lcs || []} alignment={data.alignment} canEdit={canEdit} onSaved={load} />}
      {tab === "cash" && <MoneyPanel dealId={dealId} deal={d} isStaff={canEdit} role={role} />}
      {tab === "chat" && <ConversationPanel dealId={dealId} isStaff={canEdit} role={role} />}
    </>
  );
}

/* ---------- Page ---------- */
export default function Deals() {
  const [view, setView] = useState("list");
  const [deals, setDeals] = useState([]);
  const [selected, setSelected] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [canEdit, setCanEdit] = useState(false);
  const [role, setRole] = useState("");
  const [listTab, setListTab] = useState("deals");

  useEffect(() => {
    (async () => {
      const session = await fetchAuthSession();
      const groups = session.tokens?.idToken?.payload?.["cognito:groups"] || [];
      setCanEdit(groups.includes("admin") || groups.includes("ops"));
      setRole(["admin", "ops", "seller", "buyer", "financier"].find((r) => groups.includes(r)) || "");
    })();
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try { setDeals(await api.list()); setError(""); }
    catch (e) { setError(e.message); }
    setLoading(false);
  }, []);
  useEffect(() => { loadList(); }, [loadList]);

  const open = (id) => { setSelected(id); setView("detail"); };
  const back = () => { setView("list"); loadList(); };

  if (view === "new") return <NewDeal onCancel={back} onCreated={open} />;
  if (view === "detail") return <DealDetail dealId={selected} canEdit={canEdit} role={role} onBack={back} />;

  const showPortfolio = canEdit || role === "financier";

  return (
    <>
      {showPortfolio && (
        <TabBar
          tabs={[
            { id: "deals", label: "Deals", badge: deals.length ? String(deals.length) : "" },
            { id: "portfolio", label: "Portfolio cash flow" }
          ]}
          active={listTab}
          onChange={setListTab}
        />
      )}

      {showPortfolio && listTab === "portfolio" && <PortfolioCard canEdit={canEdit} onOpen={open} />}

      {(!showPortfolio || listTab === "deals") && (
        <div className="card">
          <div style={row}>
            <h2 style={{ margin: 0 }}>Deals</h2>
            {canEdit && <button className="btn" onClick={() => setView("new")}>New deal</button>}
          </div>
          {error && <div className="err">{error}</div>}
          {loading ? <p>Loading deals...</p> : deals.length === 0 ? (
            <p className="note">
              No deals yet. {canEdit ? "Create the first deal to start tracking terms, LCs and documents." : "You will see deals here once you are added as a party."}
            </p>
          ) : (
            <div className="tablewrap">
              <table>
                <thead>
                  <tr><th>Reference</th><th>Commodity</th><th>Incoterm</th><th>Quantity (MT)</th><th>Route</th><th>Stage</th><th>Terms</th></tr>
                </thead>
                <tbody>
                  {deals.map((d) => (
                    <tr key={d.dealId}>
                      <td><button className="link" onClick={() => open(d.dealId)}>{d.dealRef}</button></td>
                      <td>{d.commodity}</td>
                      <td>{d.incoterm}</td>
                      <td>{Number(d.quantityMT).toLocaleString()}</td>
                      <td>{d.loadPort} to {d.dischargePort}</td>
                      <td><span className="badge other">{pretty(d.stage)}</span></td>
                      <td>v{d.currentVersion}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </>
  );
}